import React, { useState } from "react";
import Box from "@mui/material/Box";
import { htmlDecode } from "src/utils/convertHtml";
import { s3baseUrl } from "src/config/config";
import { getTimeBasedOnDate } from "src/utils/constants";
import {
  AddCommentAPI,
  DeleteComment,
  DeleteCommentImageOnS3,
  UpdateCommentAPI,
  UploadCommentImageOnS3,
} from "src/DAL/SupportTicket/Comments";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import { savedFileTiles, useComposerTiles } from "./ticketHelpers";
import TinyEditor from "src/components/GeneralComponents/Ckeditor";
import CustomPopover from "src/components/CustomPopover";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrCard from "src/design-system/components/GrCard";
import GrChatBubble, {
  GrChatDateDivider,
} from "src/design-system/components/GrChatBubble";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrAttachments from "src/design-system/components/GrAttachments";
import GrFormQuestion from "src/design-system/components/GrFormQuestion";
import GrButton from "src/design-system/components/GrButton";
import GrNotice from "src/design-system/components/GrNotice";
import GrIcon from "src/design-system/components/GrIcon";
import GrImageDialog from "src/design-system/components/GrImageDialog";
import { grCleanName } from "src/design-system/components/GrChatListRow";

function ChatCard({ comments, ticket_id, list_data, supportTicketData }) {
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [files, setFiles] = useState([]);
  const [oldImagesArray, setOldImagesArray] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [commentId, setCommentId] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  // Long conversations (Gmail / Zendesk pattern): the last 3 replies stay open; the ones before them
  // fold behind one button. (The ticket's own description is under the title on the page.)
  const [showAllReplies, setShowAllReplies] = useState(false);
  const [shortDescriptionCk, setShortDescriptionCk] = useState("");
  const [showButtons, setShowButtons] = React.useState({
    addButton: true,
    editButton: false,
  });
  const handleOpen = (image_path) => {
    setImageUrl(image_path);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleRemove = (index) => {
    files.splice(index, 1);
    setFiles([...files]);
  };
  const handleUpload = (event) => {
    let setImageObject = {};
    const fileList = event.target.files;
    const arr = [];

    for (let i = 0; i < fileList.length; i++) {
      setImageObject = {
        path: fileList[i],
        type: "file",
        extension: fileList[i].type,
      };

      arr.push(setImageObject);
    }
    setFiles((prevFiles) => [...prevFiles, ...arr]);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await DeleteComment(commentId);
    if (result.code === 200) {
      list_data();
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  //Getting Note In textarea
  const handleEdit = (comment) => {
    setShortDescriptionCk(comment.message);
    setCommentId(comment._id);
    setShowButtons({
      addButton: false,
      editButton: true,
    });
    // The reply's own images, fresh each time (Edit twice no longer adds them twice).
    const images = (comment.comment_image || []).map((images) => ({
      thumbnail_1: images.thumbnail_1,
      thumbnail_2: images.thumbnail_2,
      type: "image",
    }));
    setFiles(images);
    // Same objects in both lists (not copies): the update deletes `type` from them before sending,
    // exactly as before, so the saved images go out as { thumbnail_1, thumbnail_2 }.
    setOldImagesArray([...images]);
  };

  const handleCancelUpdate = () => {
    setShortDescriptionCk("");
    setCommentId("");
    setShowButtons({
      addButton: true,
      editButton: false,
    });
    setFiles([]);
    setOldImagesArray([]);
  };

  const handleAgreeDelete = (comment) => {
    setCommentId(comment._id);
    setOpenDelete(true);
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: icons.edit,
      handleClick: handleEdit,
    },

    {
      label: "Delete",
      icon: icons.deleteTwoTone,
      danger: true,
      handleClick: handleAgreeDelete,
    },
  ];

  const UploadImages = async (formData) => {
    const result = await UploadCommentImageOnS3(formData);
    return result.image_path;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const results = files.map((image, index) => {
      const formData = new FormData();
      formData.append("image", image.path);
      const result = UploadImages(formData);
      return result;
    });

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results.map((image_path, index) => {
        setImageArray.push(image_path);
      });
      const formDataObject = {
        support_ticket: ticket_id,
        message: shortDescriptionCk,
      };
      if (files.length > 0) {
        formDataObject.comment_image = setImageArray;
      }
      const result = await AddCommentAPI(JSON.stringify(formDataObject));
      if (result.code === 200) {
        setIsLoading(false);
        list_data();
        setShortDescriptionCk("");
        setFiles([]);
        enqueueSnackbar(result.message, { variant: "success" });
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setIsLoading(false);
      }
    });
  };

  const DeleteImages = async (formData) => {
    const result = await DeleteCommentImageOnS3(formData);
    return result;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const deleted_images_array = [];
    let deleted_images_object = {};
    oldImagesArray.map((old_image, index) => {
      const filtered_array = files.filter(
        (image, index) => image.thumbnail_1 == old_image.thumbnail_1
      );
      delete old_image.type;
      if (filtered_array.length == 0) {
        deleted_images_array.push(old_image);
      }
    });
    if (deleted_images_array.length > 0) {
      deleted_images_object = {
        moment_image: deleted_images_array,
      };
      DeleteImages(deleted_images_object);
    }

    const results = files.map((image, index) => {
      if (image.type === "file") {
        const formData = new FormData();
        formData.append("image", image.path);
        const result = UploadImages(formData);
        return result;
      } else {
        return image;
      }
    });

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results.map((image_path, index) => {
        setImageArray.push(image_path);
      });
      const formDataObject = {
        message: shortDescriptionCk,
      };
      if (files.length > 0) {
        formDataObject.comment_image = setImageArray;
      }
      const result = await UpdateCommentAPI(commentId, formDataObject);
      if (result.code === 200) {
        list_data();
        setShortDescriptionCk("");
        setFiles([]);
        setCommentId("");
        setShowButtons({
          addButton: true,
          editButton: false,
        });
        setIsLoading(false);
        enqueueSnackbar(result.message, { variant: "success" });
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setIsLoading(false);
      }
    });
  };

  // Day dividers and times as in Chat: "Today" / "12 Oct 2026" between days, the time in each bubble.
  const replyCount = comments?.length || 0;
  const hiddenCount = !showAllReplies && replyCount > 4 ? replyCount - 3 : 0;

  // Replies' `action_date` is the gym's clock without a zone: read as stored.
  const dayOf = (date) => getTimeBasedOnDate(date, "DD MMM YYYY");
  const timeOf = (date) => getTimeBasedOnDate(date, "hh:mm A");
  const todayDay = dayOf(new Date().toISOString());
  const divider = (date, prevDate) => {
    const day = dayOf(date);
    if (!day || (prevDate && dayOf(prevDate) === day)) return null;
    return (
      <GrChatDateDivider>{day === todayDay ? "Today" : day}</GrChatDateDivider>
    );
  };

  const composerFiles = useComposerTiles(files);

  return (
    <GrCard as="section" aria-label="Conversation">
      {comments && comments.length ? (
        <Box
          role="region"
          aria-label="Replies"
          sx={{
            pb: gr.space[5],
            mb: gr.space[5],
            borderBottom: `1px solid ${gr.color.line}`,
            "& > [role=separator]:first-of-type": { mt: 0 },
          }}
        >
          {hiddenCount > 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: gr.space[3],
              }}
            >
              <GrButton
                variant="text"
                size="sm"
                startIcon={<GrIcon icon={icons.chevronDown} />}
                onClick={() => setShowAllReplies(true)}
              >
                {`Show ${hiddenCount} earlier messages`}
              </GrButton>
            </Box>
          ) : null}
          {comments.map((comment, i) => {
            if (i < hiddenCount) return null;
            const comment_date = timeOf(comment.action_date);
            // After the fold the first shown reply always gets its day line (the ones before are hidden).
            const prevDate =
              i === hiddenCount ? null : comments[i - 1].action_date;
            const mine = comment.action_user_info.action_by === "member_user";
            const name = grCleanName(
              htmlDecode(comment.action_user_info.action_name)
            );
            const attachments = savedFileTiles(comment.comment_image);
            return (
              <React.Fragment key={comment._id ?? i}>
                {divider(comment.action_date, prevDate)}
                <GrChatBubble
                  mine={mine}
                  // Their name on top (support replies can come from different staff).
                  name={mine ? undefined : name}
                  avatar={
                    mine ? undefined : (
                      <GrAvatar
                        src={
                          comment.action_user_info.profile_image
                            ? s3baseUrl + comment.action_user_info.profile_image
                            : undefined
                        }
                        name={name}
                        size="sm"
                      />
                    )
                  }
                  time={comment_date}
                  actions={
                    mine && supportTicketData.ticket_status === 0 ? (
                      <CustomPopover menu={MENU_OPTIONS} data={comment} />
                    ) : null
                  }
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: comment.message,
                    }}
                  ></div>
                  <GrAttachments
                    files={attachments}
                    onOpen={(index) => handleOpen(attachments[index].href)}
                    sx={{ mt: attachments.length ? gr.space[2] : 0 }}
                  />
                </GrChatBubble>
              </React.Fragment>
            );
          })}
        </Box>
      ) : null}
      {supportTicketData.ticket_status !== 0 ? (
        // Closed tickets take no replies (the server refuses them): say so instead of a dead editor.
        <GrNotice
          inline
          tone="ok"
          icon={<GrIcon icon={icons.check} />}
          title="This ticket is resolved."
          text="To ask something new, create a new ticket."
        />
      ) : (
        <Box
          component="form"
          onSubmit={showButtons.addButton ? handleSubmit : handleUpdate}
        >
          <GrFormQuestion label="Enter Message" required>
            <Box
              sx={{
                mt: gr.space[3],
                // Reserve the editor's height so the page does not jump when it loads.
                minHeight: `calc(${gr.space[12]} * 5)`,
                "& .tox-tinymce": {
                  borderRadius: gr.radius.control,
                  borderColor: gr.color.line,
                },
              }}
            >
              <TinyEditor
                setDetailDescription={setShortDescriptionCk}
                detailDescriptionCk={shortDescriptionCk}
              />
            </Box>
          </GrFormQuestion>
          <GrAttachments
            hint="Recommended Size (1000x670)"
            files={composerFiles}
            onRemove={handleRemove}
            onAdd={handleUpload}
            accept="image/*,.pdf,.xlsx,.xls,.docx,.csv,.doc"
            multiple={true}
            sx={{ mt: gr.space[4] }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: gr.space[3],
              mt: gr.space[5],
            }}
          >
            {showButtons.addButton === true && (
              <GrButton type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Send"}
              </GrButton>
            )}
            {showButtons.editButton === true && (
              <>
                {/* type="button": Cancel only leaves edit mode (it used to submit the form and save the edit). */}
                <GrButton
                  variant="quiet"
                  type="button"
                  onClick={handleCancelUpdate}
                >
                  Cancel
                </GrButton>
                <GrButton type="submit" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update"}
                </GrButton>
              </>
            )}
          </Box>
        </Box>
      )}
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this comment?"}
        handleAgree={handleDelete}
      />
      <GrImageDialog open={open} onClose={handleClose} src={imageUrl} />
    </GrCard>
  );
}

export default ChatCard;
